import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getTasks, getTask, updateTask, createTask,
  getTaskComments, createTaskComment,
  getStaff, getCategories, getClients,
} from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateDdMmmYy, formatRupeesUI } from '../../utils/format';

/**
 * The phone Tasks screen.
 *
 * The desktop screen is a 15-column table with inline editing, bulk actions,
 * export and five sibling tabs (sub-tasks, litigations, certificates,
 * compliances). None of that survives 375px. What people actually do with
 * tasks away from a desk is: find one, read who/when/what, close it, or leave
 * a note. That is the whole of this screen.
 */

interface TaskRow {
  id: number;
  taskId: string;
  taskName: string;
  status: 'OPEN' | 'CLOSED';
  billingStatus?: 'BILLED' | 'UNBILLED';
  dueDate?: string | null;
  billedAmount?: number | null;
  client?: { clientName?: string } | null;
  category?: { name?: string } | null;
  partner?: { id?: number; staffName?: string; reportingPartnerId?: number } | null;
  manager?: { id?: number; staffName?: string } | null;
  reference?: string | null;
}

interface Comment {
  id: number;
  content: string;
  createdAt: string;
  author?: { staffName?: string } | null;
}

const PAGE_SIZE = 20;

const STATUS_TABS: Array<{ label: string; value: '' | 'OPEN' | 'CLOSED' }> = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'OPEN' },
  { label: 'Closed', value: 'CLOSED' },
];

/** Whole days from today; negative means overdue. */
function daysAway(iso: string): number {
  const due = new Date(iso);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

function dueLabel(iso: string | null | undefined, status: string): { text: string; tone: string } {
  if (!iso) return { text: 'No due date', tone: 'text-gray-400' };
  // A closed task's due date is history — flagging it red helps nobody.
  if (status === 'CLOSED') return { text: formatDateDdMmmYy(iso), tone: 'text-gray-500' };
  const d = daysAway(iso);
  if (d < 0) return { text: `${Math.abs(d)}d overdue`, tone: 'text-red-600 font-semibold' };
  if (d === 0) return { text: 'Due today', tone: 'text-amber-600 font-semibold' };
  return { text: formatDateDdMmmYy(iso), tone: 'text-gray-500' };
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
      status === 'OPEN' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
    }`}
  >
    {status}
  </span>
);

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value ? (
    <div className="flex items-baseline justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-400 shrink-0">{label}</span>
      <span className="text-sm text-gray-800 text-right break-words min-w-0">{value}</span>
    </div>
  ) : null;

// text-base (16px) is deliberate: iOS Safari zooms the viewport on focus for
// anything smaller, and the zoom never fully unwinds.
const INPUT = 'w-full min-h-[44px] px-3 py-2.5 text-base bg-white border border-gray-300 rounded-lg ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const LABEL = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

// ── Create sheet ─────────────────────────────────────────────────────────────

interface StaffOption { id: number; staffName: string; isPartner?: boolean; isActive?: boolean }
interface CategoryOption { id: number; name: string }
interface ClientOption { id: number; clientCode: string; clientName: string }

/**
 * New task. The desktop create modal's six fields, in the same order and with
 * the same required set — anything the server ignores (profit centre, billing
 * entity, templates) is left on the desktop where there is room for it.
 */
const CreateTaskSheet: React.FC<{ onClose: () => void; onCreated: () => void }> = ({
  onClose, onCreated,
}) => {
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);

  const [taskName, setTaskName] = useState('');
  const [clientId, setClientId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [dueDate, setDueDate] = useState(''); // ISO yyyy-mm-dd, what <input type="date"> wants

  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState<ClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([getStaff().catch(() => ({ data: [] })), getCategories().catch(() => ({ data: [] }))])
      .then(([s, c]) => {
        if (cancelled) return;
        setStaff(s.data || []);
        setCategories(c.data || []);
      });
    return () => { cancelled = true; };
  }, []);

  // Clients are searched server-side from 2 characters, as on desktop — the
  // full list is far too long to ship to a phone or scroll through.
  useEffect(() => {
    const q = clientSearch.trim();
    if (selectedClient || q.length < 2) { setClientResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      getClients(q)
        .then((r) => { if (!cancelled) setClientResults(r.data || []); })
        .catch(() => { if (!cancelled) setClientResults([]); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [clientSearch, selectedClient]);

  const partners = staff.filter((s) => s.isPartner && s.isActive !== false);
  const activeStaff = staff.filter((s) => s.isActive !== false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError('');

    // Same required set, and the same wording, as the desktop create form.
    const missing: string[] = [];
    if (!clientId) missing.push('Client');
    if (!partnerId) missing.push('Partner');
    if (!managerId) missing.push('Manager');
    if (!categoryId) missing.push('Category');
    if (!dueDate) missing.push('Due Date');
    if (missing.length > 0) {
      setError(`Required fields missing: ${missing.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      await createTask({
        taskName,
        partnerId: partnerId || null,
        managerId: managerId || null,
        clientId: clientId || null,
        categoryId: categoryId || null,
        dueDate: dueDate || null,
      });
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <header
        className="flex items-center gap-2 px-4 border-b border-gray-200 shrink-0"
        style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))', paddingBottom: '0.5rem' }}
      >
        <button
          onClick={onClose}
          className="-ml-2 px-3 h-11 text-sm font-medium text-blue-600 rounded-lg active:bg-gray-100"
        >
          ‹ Back
        </button>
        <span className="text-sm font-semibold text-gray-500">New task</span>
      </header>

      <form onSubmit={submit} className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4">
        <div>
          <label className={LABEL} htmlFor="task-name">Task name</label>
          <input
            id="task-name"
            className={INPUT}
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            placeholder="e.g. GSTR-9 filing FY 25-26"
            required
          />
        </div>

        <div>
          <label className={LABEL} htmlFor="client-search">Client</label>
          {selectedClient ? (
            <div className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedClient.clientName}</p>
                <p className="text-xs text-gray-500 truncate">{selectedClient.clientCode}</p>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedClient(null); setClientId(''); setClientSearch(''); }}
                className="min-h-[44px] px-2 text-sm text-blue-600 shrink-0"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                id="client-search"
                className={INPUT}
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Type 2+ characters to search…"
                autoComplete="off"
              />
              {clientResults.length > 0 && (
                <div className="mt-1 bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
                  {clientResults.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setSelectedClient(c);
                        setClientId(String(c.id));
                        setClientResults([]);
                      }}
                      className="w-full text-left px-3 py-3 min-h-[44px] active:bg-blue-50"
                    >
                      <p className="text-sm text-gray-900 truncate">{c.clientName}</p>
                      <p className="text-xs text-gray-500 truncate">{c.clientCode}</p>
                    </button>
                  ))}
                </div>
              )}
              {clientSearch.trim().length >= 2 && clientResults.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No client matches that search.</p>
              )}
            </>
          )}
        </div>

        <div>
          <label className={LABEL} htmlFor="task-partner">Partner</label>
          <select id="task-partner" className={INPUT} value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
            <option value="">— Select partner —</option>
            {partners.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
          </select>
        </div>

        <div>
          <label className={LABEL} htmlFor="task-manager">Manager</label>
          <select id="task-manager" className={INPUT} value={managerId} onChange={(e) => setManagerId(e.target.value)}>
            <option value="">— Select manager —</option>
            {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
          </select>
        </div>

        <div>
          <label className={LABEL} htmlFor="task-category">Category</label>
          <select id="task-category" className={INPUT} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">— Select —</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div>
          <label className={LABEL} htmlFor="task-due">Due date</label>
          <input
            id="task-due"
            type="date"
            className={INPUT}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          {dueDate && <p className="text-xs text-gray-500 mt-1">{formatDateDdMmmYy(dueDate)}</p>}
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}

        <button
          type="submit"
          disabled={saving}
          className="w-full min-h-[44px] rounded-xl bg-blue-600 text-white text-sm font-semibold active:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Creating…' : 'Create task'}
        </button>
      </form>
    </div>
  );
};

// ── Detail sheet ─────────────────────────────────────────────────────────────

const TaskSheet: React.FC<{ taskId: number; onClose: () => void; onChanged: () => void }> = ({
  taskId, onClose, onChanged,
}) => {
  const { user, isAdmin, isHR } = useAuth();
  const [task, setTask] = useState<TaskRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTask(taskId)
      .then((r) => { if (!cancelled) { setTask(r.data); setError(''); } })
      .catch((e: any) => { if (!cancelled) setError(e.response?.data?.message || 'Could not load this task'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    getTaskComments(taskId)
      .then((r) => { if (!cancelled) setComments(r.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [taskId]);

  // Mirrors canUpdateTask on the desktop screen, which mirrors the server's
  // own check — the server is still the authority, so a rejected save is
  // surfaced rather than assumed impossible.
  const canUpdate = !!task && (
    isAdmin || isHR ||
    user?.staffId === task.partner?.id ||
    user?.staffId === task.manager?.id ||
    task.partner?.reportingPartnerId === user?.staffId
  );

  const canSeeBilling = !!task && (
    isAdmin || isHR ||
    user?.staffId === task.partner?.id ||
    user?.staffId === task.manager?.id ||
    task.partner?.reportingPartnerId === user?.staffId
  );

  const toggleStatus = async () => {
    if (!task || statusSaving) return;
    const next = task.status === 'OPEN' ? 'CLOSED' : 'OPEN';
    setStatusSaving(true);
    setError('');
    try {
      await updateTask(task.id, { status: next });
      setTask({ ...task, status: next });
      onChanged();
    } catch (e: any) {
      // Closing can be refused server-side (archive link missing, manager
      // hasn't confirmed archiving). That message is the useful one.
      setError(e.response?.data?.message || 'Could not change the status');
    } finally {
      setStatusSaving(false);
    }
  };

  const postComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || posting || !commentText.trim()) return;
    setPosting(true);
    try {
      await createTaskComment(task.id, { content: commentText.trim() });
      setCommentText('');
      const r = await getTaskComments(task.id);
      setComments(r.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not post that note');
    } finally {
      setPosting(false);
    }
  };

  const due = task ? dueLabel(task.dueDate, task.status) : null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <header
        className="flex items-center gap-2 px-4 border-b border-gray-200 shrink-0"
        style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))', paddingBottom: '0.5rem' }}
      >
        <button
          onClick={onClose}
          className="-ml-2 px-3 h-11 text-sm font-medium text-blue-600 rounded-lg active:bg-gray-100"
        >
          ‹ Back
        </button>
        <span className="text-sm font-semibold text-gray-500 truncate">{task?.taskId ?? 'Task'}</span>
      </header>

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4">
        {loading ? (
          <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
        ) : !task ? (
          <p className="text-sm text-red-600 text-center py-8">{error || 'Task not found'}</p>
        ) : (
          <>
            <div>
              <div className="flex items-start justify-between gap-3">
                <h1 className="text-lg font-bold text-gray-900 break-words min-w-0">{task.taskName}</h1>
                <StatusPill status={task.status} />
              </div>
              {due && <p className={`text-xs mt-1 ${due.tone}`}>{due.text}</p>}
            </div>

            {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

            <div className="bg-white border border-gray-200 rounded-xl px-4 py-1">
              <Field label="Client" value={task.client?.clientName} />
              <Field label="Category" value={task.category?.name} />
              <Field label="Partner" value={task.partner?.staffName} />
              <Field label="Manager" value={task.manager?.staffName} />
              <Field label="Due" value={task.dueDate ? formatDateDdMmmYy(task.dueDate) : null} />
              <Field label="Reference" value={task.reference} />
              {canSeeBilling && (
                <>
                  <Field label="Billing" value={task.billingStatus} />
                  <Field
                    label="Billed"
                    value={task.billedAmount ? `₹${formatRupeesUI(task.billedAmount)}` : null}
                  />
                </>
              )}
            </div>

            {canUpdate && (
              <button
                onClick={toggleStatus}
                disabled={statusSaving}
                className={`w-full min-h-[44px] rounded-xl text-sm font-semibold text-white disabled:opacity-60 ${
                  task.status === 'OPEN' ? 'bg-blue-600 active:bg-blue-700' : 'bg-gray-600 active:bg-gray-700'
                }`}
              >
                {statusSaving ? 'Saving…' : task.status === 'OPEN' ? 'Mark as closed' : 'Reopen task'}
              </button>
            )}

            <section>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Notes</h2>
              <div className="space-y-2">
                {comments.length === 0 && <p className="text-sm text-gray-400">No notes yet</p>}
                {comments.map((c) => (
                  <div key={c.id} className="bg-gray-50 rounded-xl px-3 py-2">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{c.content}</p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {c.author?.staffName || 'Unknown'} · {formatDateDdMmmYy(c.createdAt)}
                    </p>
                  </div>
                ))}
              </div>

              <form onSubmit={postComment} className="mt-3 space-y-2">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={3}
                  placeholder="Add a note…"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={posting || !commentText.trim()}
                  className="w-full min-h-[44px] rounded-xl bg-blue-600 text-white text-sm font-semibold active:bg-blue-700 disabled:opacity-40"
                >
                  {posting ? 'Posting…' : 'Add note'}
                </button>
              </form>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

// ── List ─────────────────────────────────────────────────────────────────────

const MobileTasks: React.FC = () => {
  const [searchParams] = useSearchParams();
  // Deep links arrive in two shapes: ?openTask=42 (numeric id, from the
  // desktop links) and ?taskId=TSK-0042 (the human code, from MobileHome).
  // The second can only be honoured as a search, since the list API filters
  // on text, not on id.
  const deepTaskCode = searchParams.get('taskId') ?? '';
  const deepOpenId = Number(searchParams.get('openTask')) || null;

  const [search, setSearch] = useState(deepTaskCode);
  const [debouncedSearch, setDebouncedSearch] = useState(deepTaskCode);
  const [status, setStatus] = useState<'' | 'OPEN' | 'CLOSED'>(deepTaskCode ? '' : 'OPEN');
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<number | null>(deepOpenId);
  const [creating, setCreating] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Any change of filter restarts paging; "load more" only ever appends.
  useEffect(() => { setPage(1); }, [debouncedSearch, status]);

  useEffect(() => {
    let cancelled = false;
    const first = page === 1;
    if (first) setLoading(true); else setLoadingMore(true);

    const params: any = { page, pageSize: PAGE_SIZE, sortField: 'dueDate', sortDir: 'asc' };
    if (debouncedSearch) params.search = debouncedSearch;
    if (status) params.status = status;

    getTasks(params)
      .then((r) => {
        if (cancelled) return;
        const fetched: TaskRow[] = r.data?.rows ?? [];
        setRows((prev) => (first ? fetched : [...prev, ...fetched]));
        setTotal(r.data?.total ?? fetched.length);
        setError('');
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e.response?.data?.message || 'Could not load tasks');
        if (first) setRows([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setLoadingMore(false);
      });

    return () => { cancelled = true; };
  }, [debouncedSearch, status, page, refreshTick]);

  const reload = useCallback(() => {
    setPage(1);
    setRefreshTick((t) => t + 1);
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Tasks</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {loading ? 'Loading…' : `${total} task${total === 1 ? '' : 's'}`}
        </p>
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search tasks, clients, IDs…"
        className="w-full min-h-[44px] border border-gray-300 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      <div className="grid grid-cols-3 gap-1 bg-gray-100 rounded-xl p-1">
        {STATUS_TABS.map((t) => (
          <button
            key={t.label}
            onClick={() => setStatus(t.value)}
            className={`min-h-[36px] rounded-lg text-sm font-medium transition-colors ${
              status === t.value ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <div className="space-y-2">
        {loading ? (
          <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            {debouncedSearch ? 'No tasks match that search' : 'No tasks here'}
          </p>
        ) : (
          rows.map((t) => {
            const due = dueLabel(t.dueDate, t.status);
            return (
              <button
                key={t.id}
                onClick={() => setOpenId(t.id)}
                className="w-full text-left bg-white border border-gray-200 rounded-xl px-4 py-3 active:bg-gray-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900 break-words min-w-0">{t.taskName}</p>
                  <StatusPill status={t.status} />
                </div>
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  {t.taskId}
                  {t.client?.clientName ? ` · ${t.client.clientName}` : ''}
                </p>
                <p className={`text-xs mt-1 ${due.tone}`}>{due.text}</p>
              </button>
            );
          })
        )}
      </div>

      {!loading && rows.length < total && (
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={loadingMore}
          className="w-full min-h-[44px] rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 active:bg-gray-50 disabled:opacity-60"
        >
          {loadingMore ? 'Loading…' : `Load more (${total - rows.length} left)`}
        </button>
      )}

      {/* Ungated on purpose: POST /tasks is behind `authenticate` only, and the
          desktop's "+ Add Task" is shown to everyone too. Gating it here would
          hide something the server in fact permits. */}
      <button
        onClick={() => setCreating(true)}
        className="fixed right-4 z-30 w-14 h-14 rounded-full bg-blue-600 text-white text-2xl shadow-lg flex items-center justify-center active:bg-blue-700"
        style={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
        aria-label="New task"
      >
        +
      </button>

      {creating && (
        <CreateTaskSheet
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            // A brand-new task is OPEN and sorted by due date, so the default
            // filters will show it; a search in progress might not, so it is
            // cleared to guarantee the new row is visible.
            setSearch('');
            setStatus('OPEN');
            reload();
          }}
        />
      )}

      {openId !== null && (
        <TaskSheet taskId={openId} onClose={() => setOpenId(null)} onChanged={reload} />
      )}
    </div>
  );
};

export default MobileTasks;
